import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { applyConsent, readConsent } from '../utils/analytics';
import { colors, layout, radii } from '../theme/tokens';

/**
 * Minimal Consent Mode v2 banner (required for Google Ads measurement in the UK/EEA).
 * gtag starts in "denied" mode (see index.html); accepting upgrades the signals.
 */
const ConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      applyConsent(stored);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  if (!visible) return null;

  const choose = (choice: 'granted' | 'denied') => {
    applyConsent(choice);
    setVisible(false);
  };

  return (
    <Box
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      sx={{
        position: 'fixed',
        left: { xs: 12, sm: 24 },
        right: { xs: 12, sm: 'auto' },
        bottom: { xs: `calc(${layout.mobileBarHeight}px + 12px)`, md: 24 },
        zIndex: 1350,
        maxWidth: 420,
        p: 2.5,
        borderRadius: radii.lg,
        backgroundColor: 'rgba(24,24,24,0.96)',
        backdropFilter: 'blur(14px)',
        border: `1px solid ${colors.borderStrong}`,
        boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
      }}
    >
      <Typography sx={{ color: colors.text, fontWeight: 600, mb: 0.5 }}>Cookies on this site</Typography>
      <Typography sx={{ color: colors.textMuted, fontSize: '0.9rem', lineHeight: 1.6, mb: 2 }}>
        We use analytics cookies to understand how people find us and improve the site. No personal data is sold.{' '}
        <Box component={RouterLink} to="/privacy" sx={{ color: colors.white, fontWeight: 600 }}>
          Privacy policy
        </Box>
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" size="small" onClick={() => choose('granted')} sx={{ flex: 1 }}>
          Accept
        </Button>
        <Button variant="outlined" size="small" onClick={() => choose('denied')} sx={{ flex: 1 }}>
          Essential only
        </Button>
      </Box>
    </Box>
  );
};

export default ConsentBanner;
